<template>
  <page-wrapper ref="pageEl" :title="title" :has-pad="true">
    <div class="mb-4 text-end">
      <sc-button
        :route="{ name: 'AppointmentCreatePage' }"
        class="btn-create"
        variant="secondary"
        hidden
        >นัดวันแทนลูกบ้าน</sc-button
      >
      <sc-button :route="{ name: 'AppointmentListPage' }" variant="primary"
        >ไปหน้าค้นหา</sc-button
      >
    </div>

    <div class="row mb-4">
      <div class="col-lg-3">
        <el-select
          :value="statusQuery"
          multiple
          placeholder="เลือกสถานะ"
          clearable
          @input="handleChangeStatusQuery"
        >
          <el-option
            v-for="item in statusOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          ></el-option>
        </el-select>
      </div>
      <div class="col-lg-3">
        <el-select
          :value="readQuery"
          placeholder="เลือกสถานะการอ่าน"
          clearable
          @input="handleChangeReadQuery"
        >
          <el-option
            v-for="item in readOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          ></el-option>
        </el-select>
      </div>
    </div>
    <p v-if="updatedAppointments.length" class="mb-2">
      {{ totalCount }}
    </p>
    <div class="alert alert-warning download-report-message">
      เลือก ช่วงเวลา ที่ต้องการก่อนกด Download Report
    </div>
    <div class="row mb-4">
      <div class="ms-3">
        <el-date-picker
          :value="dateTimeReport"
          class="date-picker"
          type="daterange"
          range-separator="ถึง"
          @input="handleChangeDate($event)"
        ></el-date-picker>
      </div>
      <div class="ms-3">
        <sc-button variant="secondary" @click="handleClickDownloadReport"
          >Download Report</sc-button
        >
      </div>
    </div>
    <updated-appointment-row
      v-for="appointment in updatedAppointments"
      v-bind="appointment"
      :key="appointment.id"
      @read="handleReadAppointment(appointment)"
    ></updated-appointment-row>
    <div v-if="!updatedAppointments.length" class="text-center p-4">
      ไม่มีข้อมูล
    </div>
    <div class="text-end mt-4">
      <el-pagination
        :total="total"
        :page-size="10"
        :current-page="currentPage"
        background
        layout="prev, pager, next"
        @current-change="handleCurrentChange"
      ></el-pagination>
    </div>
  </page-wrapper>
</template>

<script>
import { Select, Option, Pagination, DatePicker, MessageBox } from "element-ui"
import PageWrapper from "@/lib/components/PageWrapper"
import ScButton from "@/lib/components/ScButton"
import { DateTime } from "luxon"
import { baanRueJaiClient } from "@/dependencies"
import { convertBathToStangAndRoundTwoDecimal } from "@/lib/formatter"
import UpdatedAppointmentRow from "../components/UpdatedAppointmentRow"

const SERVICE_TYPE = "HOMEOS"

export default {
  components: {
    PageWrapper,
    UpdatedAppointmentRow,
    ScButton,
    "el-select": Select,
    "el-option": Option,
    "el-pagination": Pagination,
    "el-date-picker": DatePicker,
  },
  data() {
    const title = "นัดหมายล่าสุด"
    const updatedAppointments = []
    const statusOptions = [
      { label: "นัดใหม่", value: "CREATED" },
      { label: "ยืนยัน", value: "CONFIRMED" },
      { label: "ยกเลิก", value: "CANCELLED" },
      { label: "เสร็จสิ้น", value: "FULFILLED" },
    ]
    const readOptions = [
      { label: "อ่านแล้ว", value: "READ" },
      { label: "ยังไม่อ่าน", value: "NOT_READ" },
    ]
    const total = 0
    const selectedDateTimeReport = null

    return {
      title,
      updatedAppointments,
      statusOptions,
      readOptions,
      total,
      selectedDateTimeReport,
    }
  },
  computed: {
    currentPage() {
      return parseInt(this.$route.query.page, 10) || 1
    },
    totalCount() {
      const limit = 10
      const { total, currentPage } = this

      if (!total) {
        return `แสดงผล 0 - 0 จากทั้งหมด 0`
      }

      const endNum = Math.min(currentPage * limit, total)
      const startNum = (currentPage - 1) * limit + 1

      return `แสดงผล ${startNum.toLocaleString()} - ${endNum.toLocaleString()} จากทั้งหมด ${total.toLocaleString()}`
    },
    readQuery() {
      const { isRead = null } = this.$route.query

      if (isRead === null) {
        return null
      }

      if (isRead) {
        return "READ"
      }

      return "NOT_READ"
    },
    statusQuery() {
      const { status = [] } = this.$route.query

      return status
    },
    dateTimeReport() {
      if (!this.selectedDateTimeReport) {
        return null
      }

      const { from, to } = this.selectedDateTimeReport

      return [from.setZone("local").toJSDate(), to.setZone("local").toJSDate()]
    },
  },
  watch: {
    "$route.query": async function watchRouteQuery() {
      await this.setupData()

      const pageContent = this.$refs.pageEl?.$el?.querySelector(".page-content")

      if (pageContent) pageContent.scrollTop = 0
    },
  },
  created() {
    this.setup()
  },
  methods: {
    async setup() {
      await this.setupData()
    },
    async setupData() {
      const response =
        await baanRueJaiClient.admin.subscriptions.appointments.getNotices({
          servicetype: SERVICE_TYPE,
          ...this.$route.query,
        })
      const { count, notices } = response.data.result

      this.total = count
      this.updatedAppointments = notices.map((notice) =>
        this.mapNoticeToRow(notice),
      )
    },
    mapNoticeToRow(notice) {
      const { appointment, id, status } = notice
      const {
        receipt_date: currentReceiptDate,
        receipt_number: receiptNumber,
        actual_cost: rawActualCost,
        main_option: mainOption,
        extra_option: extraOption,
      } = appointment

      return {
        id,
        service: appointment.service.name,
        provider: appointment.provider.name,
        user: {
          name: `${appointment.user.first_name} ${appointment.user.last_name}`,
          phoneNumber: appointment.user.mobile_number,
        },
        home: {
          addressNo: appointment.home ? appointment.home.address_no : "",
          project: appointment.project
            ? `${appointment.project.project_code} โครงการ${appointment.project.project_name}`
            : "",
        },
        dateTime: DateTime.fromISO(appointment.date_time),
        status,
        servicetype: SERVICE_TYPE,
        isRead: !!notice.read_at,
        route: {
          name: "AppointmentDetailPage",
          params: { id: appointment.id },
        },
        appointmentId: notice.appointment_id,
        currentReceiptDate,
        receiptNumber,
        actualCost: convertBathToStangAndRoundTwoDecimal(rawActualCost),
        mainOption: mainOption || null,
        extraOption: extraOption || null,
      }
    },
    async handleReadAppointment(appointment) {
      try {
        await MessageBox.confirm("ยืนยันว่าอ่านแล้ว", {
          confirmButtonText: "ตกลง",
          cancelButtonText: "ยกเลิก",
        })
      } catch (e) {
        return
      }

      const ids = [appointment.id]
      await baanRueJaiClient.admin.subscriptions.appointments.postNoticesRead({
        ids,
      })

      this.updatedAppointments = this.updatedAppointments.map((app) => {
        const isRead = app.id === appointment.id

        return isRead
          ? {
              ...app,
              isRead: true,
            }
          : app
      })
    },
    handleCurrentChange(page) {
      const route = {
        name: this.$route.name,
        query: { ...this.$route.query, page },
      }

      this.$router.replace(route)
    },
    handleChangeReadQuery(value) {
      const route = {
        name: this.$route.name,
        query: { ...this.$route.query, page: 1 },
      }

      if (!value) {
        delete route.query.isRead
      } else {
        route.query.isRead = value === "READ"
      }

      this.$router.replace(route)
    },
    handleChangeStatusQuery(status) {
      const route = {
        name: this.$route.name,
        query: { ...this.$route.query, status, page: 1 },
      }

      this.$router.replace(route)
    },
    handleChangeDate(value) {
      if (!value) {
        this.selectedDateTimeReport = null
        return
      }
      const [fromJSDate, toJSDate] = value
      const from = DateTime.fromJSDate(fromJSDate).startOf("day").toUTC()
      const to = DateTime.fromJSDate(toJSDate).endOf("day").toUTC()

      this.selectedDateTimeReport = { from, to }
    },
    async handleClickDownloadReport() {
      if (!this.selectedDateTimeReport) {
        this.$message.warning(
          "กรุณาเลือกช่วงเวลาที่ต้องการก่อนกด Download Report",
        )

        return
      }

      const { from, to } = this.selectedDateTimeReport

      try {
        const response =
          await baanRueJaiClient.admin.subscriptions.appointments.getReport({
            start_at: from.toISO(),
            end_at: to.toISO(),
          })

        const blob = new Blob([response.data])
        const link = document.createElement("a")
        link.href = window.URL.createObjectURL(blob)
        link.download = `fullfill-appointment-${from.toISODate()}-${to.toISODate()}.xlsx`
        link.click()
      } catch (error) {
        // TODO: use alert message from response message backend
        this.$message.error(
          "ไม่สามารถ Download Report ได้ เนื่องจากไม่มีข้อมูลหรือเกิดข้อผิดพลาด",
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.btn-create {
  float: left;
}

.download-report-message {
  font-size: 15px;
}
</style>
